import { Emitter, LinkedListContainer, PathParticle } from "pixi-particles";
import App from "../App";
import Layout from "../utils/Layout";
import { Container, Point, ParticleContainer,Texture } from "pixi.js";

const sharpness = 0.1;
const minDelta = 0.05;

export default class ColorfulTrail extends Container {
    private app: App;
    private emitterPos: Point;
    private targetPos: Point;
    private emitter: Emitter;
    private callback: () => void;

    constructor(
        startPos: Point,
        endPos: Point,
        callback: () => void = null,
        //startColor: string = "#feffa3",
        //endColor: string = "#f0ff00",
        startColor: string = "#ffffff",
        endColor: string = "#ffffff",
        useLinkedListContainer: boolean = true
    ) {
        super();

        this.app = App.instance;
        this.callback = callback;

        const texture = this.createTexture(0, 8 * Layout.globalScale, this.app.renderer.resolution);

        this.targetPos = endPos;

        this.emitterPos = startPos;

        let container: Container = undefined;
        if (useLinkedListContainer) {
            container = new LinkedListContainer();
        } else {
            container = new ParticleContainer(5000, {
                position: true,
                rotation: false,
                uvs: false,
                tint: true,
            });
        }

        
        this.emitter = new Emitter(container, [texture], {
            autoUpdate: true,
            alpha: {
                start: 0.8,
                end: 0.15,
            },
            scale: {
                start: 1,
                end: 0.2,
                minimumScaleMultiplier: 1,
            },
            color: {
                start: startColor,
                end: endColor,
            },
            speed: {
                start: 0,
                end: 0,
                minimumSpeedMultiplier: 1,
            },
            acceleration: {
                x: 0,
                y: 0,
            },
            maxSpeed: 0,
            startRotation: {
                min: 0,
                max: 0,
            },
            noRotation: true,
            rotationSpeed: {
                min: 0,
                max: 0,
            },
            lifetime: {
                min: 0.6,
                max: 0.6,
            },
            blendMode: "normal",
            frequency: 0.002,
            emitterLifetime: -1,
            maxParticles: 3000,
            pos: {
                x: 0,
                y: 0,
            },
            addAtBack: false,
            spawnType: "point"
        });
        
        this.emitter.updateOwnerPos(this.emitterPos.x, this.emitterPos.y);

        this.addChild(container);
        this.app.ticker.add(this.onTick, this);
    }

    private onTick(delta: number) {
        if (!this.isCloseEnough(this.emitterPos, this.targetPos)) {
            const dt = 1 - Math.pow(1 - sharpness, delta);
            const dx = this.targetPos.x - this.emitterPos.x;
            const dy = this.targetPos.y - this.emitterPos.y;

            if (Math.abs(dx) > minDelta) {
                this.emitterPos.x += dx * dt;
            } else {
                this.emitterPos.x = this.targetPos.x;
            }

            if (Math.abs(dy) > minDelta) {
                this.emitterPos.y += dy * dt;
            } else {
                this.emitterPos.y = this.targetPos.y;
            }

            this.emitter.updateOwnerPos(this.emitterPos.x, this.emitterPos.y);
        } else {
            this.onComplete();
        }
    }

    private isCloseEnough(p1: Point, p2: Point): boolean {
        return Math.abs(p1.x - p2.x) < 3 && Math.abs(p1.y - p2.y) < 3;
    }

    private onComplete() {
        this.app.ticker.remove(this.onTick, this);
        this.emitter.destroy();
        if (this.parent) {
            this.parent.removeChild(this);
        }

        this.destroy({children: true, texture: true, baseTexture: true});

        if (this.callback) {
            this.callback();
        }
    }

    private createTexture(r1: number, r2: number, resolution: number) {
        const c = (r2 + 1) * resolution;
        r1 *= resolution;
        r2 *= resolution;

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = canvas.height = Math.max(8, (c * 2) * Layout.globalScale);

        const gradient = context.createRadialGradient(c, c, r1, c, c, r2);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(1, "rgba(255,255,255,0)");

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        return Texture.from(canvas);
    }
}
